import { useState, useRef, useEffect } from 'react';

import PropTypes from 'prop-types';
import { BsChevronDown, BsCheck } from 'react-icons/bs';

import { fieldClasses } from '#globalComponents/Forms/fieldStyles';
import FormTextInput from '#globalComponents/Forms/TextInput';

const normalizeOption = (option) =>
	typeof option === 'string' ? { value: option, label: option } : { group: null, ...option };

const matchesSearch = (option, search) => !search || option.label.toLowerCase().includes(search.toLowerCase());

const optionTextClass = (isSelected, isDisabled) => {
	if (isDisabled) return 'text-bgBase-700 cursor-not-allowed';
	return isSelected ? 'text-primary-700 font-medium hover:bg-gray-50' : 'text-text hover:bg-gray-50';
};

// A disabled option that is still selected stays clickable, so it can be removed. aria-disabled
// instead of disabled: browsers suppress the tooltip of a disabled button.
const OptionButton = ({ option, isSelected, onToggle }) => {
	const isDisabled = Boolean(option.disabled) && !isSelected;
	return (
		<button
			type="button"
			onClick={() => !isDisabled && onToggle(option.value)}
			aria-disabled={isDisabled}
			title={option.title}
			className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left ${optionTextClass(isSelected, isDisabled)}`}
		>
			<span
				className={`w-3.5 h-3.5 flex-shrink-0 rounded border flex items-center justify-center ${isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300'}`}
			>
				{isSelected && <BsCheck className="w-2.5 h-2.5 text-white" />}
			</span>
			{option.label}
		</button>
	);
};

OptionButton.propTypes = {
	option: PropTypes.shape({
		value: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		disabled: PropTypes.bool,
		title: PropTypes.string,
	}).isRequired,
	isSelected: PropTypes.bool.isRequired,
	onToggle: PropTypes.func.isRequired,
};

/**
 * Multi-select dropdown; `onChange` receives the toggled value. Options with a `group` are shown
 * under a heading per group (in the given order); `searchable` adds a filter field. A `disabled`
 * option is shown greyed out with its `title` as tooltip and cannot be picked.
 */
export const MultiSelect = ({ options, selected, onChange, placeholder, searchable = false }) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const ref = useRef(null);

	useEffect(() => {
		const handler = (e) => {
			if (ref.current && !ref.current.contains(e.target)) setOpen(false);
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, []);

	// Escape closes the list only; the surrounding modal listens on document and must not
	// see the same key.
	const handleKeyDown = (e) => {
		if (e.key !== 'Escape' || !open) return;
		e.stopPropagation();
		setOpen(false);
	};

	const visibleOptions = options.map(normalizeOption).filter((option) => matchesSearch(option, search));

	return (
		<div className="relative" ref={ref} onKeyDown={handleKeyDown}>
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className={fieldClasses(
					'h-10 rounded-card px-4 flex items-center justify-between gap-2 text-left',
					false,
				)}
			>
				<span className={selected.length ? 'text-text truncate' : 'text-bgBase-800'}>
					{selected.length ? `${selected.length} ausgewählt` : placeholder}
				</span>
				<BsChevronDown
					className={`w-3.5 h-3.5 text-bgBase-700 transition-transform ${open ? 'rotate-180' : ''}`}
				/>
			</button>
			{open && (
				<div
					className={`absolute z-40 w-full mt-1 bg-white border border-bgBase-500 rounded-card shadow-elevated ${searchable ? 'max-h-64' : 'max-h-52'} overflow-y-auto py-1`}
				>
					{searchable && (
						<div className="sticky top-0 bg-white px-2 pb-1">
							<FormTextInput
								placeholder="Suchen …"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className="!h-8 text-xs"
							/>
						</div>
					)}
					{visibleOptions.length === 0 && (
						<div className="px-2 py-3 text-xs text-bgBase-800 text-center">Keine Treffer</div>
					)}
					{visibleOptions.map((option, index) => (
						<div key={option.value}>
							{option.group && option.group !== visibleOptions[index - 1]?.group && (
								<div className="px-2 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-bgBase-800">
									{option.group}
								</div>
							)}
							<OptionButton
								option={option}
								isSelected={selected.includes(option.value)}
								onToggle={onChange}
							/>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

MultiSelect.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.shape({
				value: PropTypes.string.isRequired,
				label: PropTypes.string.isRequired,
				group: PropTypes.string,
				disabled: PropTypes.bool,
				title: PropTypes.string,
			}),
		]),
	).isRequired,
	selected: PropTypes.arrayOf(PropTypes.string).isRequired,
	onChange: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
	searchable: PropTypes.bool,
};
